/**
 * Projection for `SosReportScene` — the demo's `buildSOS()` (line 2635) and
 * `renderSOS()` (line 2705).
 *
 * Pure: no React, no fetching, no engine mutation, no `Date`. Every weekly
 * figure comes from `mineWeek()` in `shared/oem-engine.ts`; this file scopes the
 * report to one site or to all six, assembles the four sections the original
 * daily report had, and writes the label strings the demo writes.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/reports/SosReportScene.tsx
 * @see docs/logic/reports/sos.md
 * @see docs/foundations/4-layer-architecture.md
 */

import { BY_ID, FLEET, MINES, MINE_ORDER } from '../../data/core';
import type { ComponentHealth } from '../../data/core';
import type { Alert } from '../../schema/alert';
import type { Incident } from '../../shared/seed/incidents';
import type { ConditionBand, Machine, MineCode } from '../../schema/machine';
import { ALERTS } from '../../shared/seed/alerts';
import { INCIDENTS } from '../../shared/seed/incidents';
import {
  healthBand,
  healthOf,
  mineWeek,
  statusOf,
  WK,
  type InventoryCtx,
  type MineWeek,
  type ShiftCtx,
  type WorkOrderCtx,
} from '../../shared/oem-engine';
import type {
  CustomerSatisfactionResponse,
  InventoryLine,
  PartOrder,
  TechnicianJob,
  TechnicianPresence,
  WorkOrder,
} from '../../shared/useOemData';

/** Demo line 2635. The morning the report is compiled. */
export const SOS_TODAY = '2026-09-09';
/** Demo line 2635. "Since yesterday" for the activity section. */
export const SOS_YESTERDAY = '2026-09-08';
/** Demo line 2638. "Since Sunday" for the closed-work-order count. */
export const SOS_SINCE_SUNDAY = '2026-09-07';

/** Demo line 2705. The document id, per scope. */
export const sosDocId = (mine: MineCode | null): string =>
  `SOS-${SOS_TODAY.replace(/-/g, '')}${mine ? `-${mine}` : ''}`;

/** Everything the report reads. */
export interface SosCtx extends WorkOrderCtx, ShiftCtx, InventoryCtx {
  readonly orders: readonly PartOrder[];
  readonly techJobs: readonly TechnicianJob[];
  readonly techs: readonly TechnicianPresence[];
  readonly css: readonly CustomerSatisfactionResponse[];
}

/** Demo line 2635. One hotspot machine. */
export interface Hotspot {
  readonly machine: Machine;
  /** The machine's lowest-scoring component. */
  readonly worst: ComponentHealth;
  readonly band: ConditionBand;
  readonly siteShort: string;
  /** The drafted work order on this machine, when there is one. */
  readonly draft: WorkOrder | undefined;
  readonly alert: Alert | undefined;
  /** `'Approve WO-26-11912 · Dale Whitmore'`, `'inside 5 days'` or the default. */
  readonly next: string;
  /** The first sentence of the register note, ending in a full stop. */
  readonly note: string;
}

/** Demo line 2636. Machines in the very-poor or major-issue bands, worst first. */
export function hotspots(sites: readonly MineCode[], ctx: SosCtx): Hotspot[] {
  return FLEET.filter(
    (f) => sites.includes(f.mine) && ['major', 'vpoor'].includes(statusOf(f.machine_id)),
  )
    .map((f) => {
      const worst = healthOf(f.machine_id)
        .slice()
        .sort((a, b) => a.health_score - b.health_score)[0];
      const draft = ctx.workOrders.find(
        (w) => w.machine_id === f.machine_id && w.status.startsWith('Draft'),
      );
      const alert = ALERTS.find((a) => a.machine === f.machine_id);
      return {
        machine: f,
        worst,
        band: healthBand(worst),
        siteShort: MINES[f.mine].name.split(' ')[0],
        draft,
        alert,
        // The demo writes `Approve <wo_id> · <technician>`, but a work order
        // carries no technician — the field is `undefined` and the demo prints
        // the word. The crew that owns the order is what the register does
        // have, so that is what the owner column says.
        next: draft
          ? `Approve ${draft.wo_id} · ${draft.crew}`
          : alert
            ? alert.window
            : 'Inspect at next PM',
        note: `${(worst.note ?? '').split('. ')[0]}.`,
      };
    })
    .sort((a, b) => a.worst.health_score - b.worst.health_score);
}

/** Demo line 2637. One row of the activity table. */
export interface ActivityRow {
  readonly what: string;
  readonly count: number;
  readonly detail: string;
  readonly key: string;
}

/** Demo line 2637. What happened in the last 24 hours. */
export function activity(sites: readonly MineCode[], ctx: SosCtx): ActivityRow[] {
  const siteNames = sites.map((k) => MINES[k].name);
  const jobsDone = ctx.techJobs.filter(
    (j) =>
      j.status === 'Completed' &&
      j.off_site != null &&
      j.off_site >= SOS_YESTERDAY &&
      siteNames.includes(j.site),
  );
  const closed = ctx.workOrders.filter(
    (w) =>
      sites.includes(w.mine) &&
      w.wo_type !== 'Preventive' &&
      w.status.startsWith('Closed') &&
      w.created >= SOS_SINCE_SUNDAY,
  );
  const alerts = ALERTS.filter((a) => sites.includes(BY_ID[a.machine].mine));
  const incidents = scopedIncidents(sites);
  const techs = ctx.techs.filter((t) => sites.includes(t.base));

  return [
    {
      key: 'jobs',
      what: 'Technician-app jobs completed',
      count: jobsDone.length,
      detail:
        jobsDone
          .map((j) => `${j.job_id} ${j.machine} (${j.technician.split(' ')[0]})`)
          .join(' · ') || '—',
    },
    {
      key: 'closed',
      what: 'Reactive work orders closed since Sunday',
      count: closed.length,
      detail: closed.slice(0, 6).map((w) => `${w.wo_id} ${w.machine_id}`).join(' · ') || '—',
    },
    {
      key: 'alerts',
      what: 'Alerts open',
      count: alerts.length,
      detail: alerts.map((a) => `${a.machine} ${a.short ?? a.comp.toLowerCase()}`).join(' · ') || '—',
    },
    {
      key: 'incidents',
      what: 'Incidents this fortnight',
      count: incidents.length,
      detail: incidents.map((i) => `${i.id} ${i.machine} — ${i.title}`).join(' · ') || '—',
    },
    {
      key: 'techs',
      what: 'Technicians on site',
      count: techs.filter((t) => t.status === 'On site').length,
      detail:
        techs
          .map(
            (t) =>
              `${t.technician.split(' ')[0]} (${t.base}${
                t.status !== 'On site' ? `, ${t.status.toLowerCase()}` : ''
              })`,
          )
          .join(' · ') || '—',
    },
  ];
}

/** Demo line 2641. The two incidents that carry a report. */
export const scopedIncidents = (sites: readonly MineCode[]): Incident[] =>
  INCIDENTS.filter((i) => sites.includes(i.mine as MineCode) && i.id !== 'WO-26-11842');

/** Demo line 2639. One row of the work-in-progress table. */
export interface WipRow {
  readonly queue: string;
  readonly count: number;
  readonly detail: string;
  readonly key: string;
}

/** The rows behind the work-in-progress counts, kept for the source drawers. */
export interface WipSlices {
  readonly drafts: readonly WorkOrder[];
  readonly requisitions: readonly PartOrder[];
  readonly inTransit: readonly PartOrder[];
  readonly openJobs: readonly TechnicianJob[];
  readonly belowMin: readonly InventoryLine[];
}

/** Demo line 2639. What is waiting on somebody, and its rows. */
export function workInProgress(
  sites: readonly MineCode[],
  ctx: SosCtx,
): { rows: WipRow[]; slices: WipSlices } {
  const siteNames = sites.map((k) => MINES[k].name);
  const slices: WipSlices = {
    drafts: ctx.workOrders.filter((w) => sites.includes(w.mine) && w.status.startsWith('Draft')),
    requisitions: ctx.orders.filter((o) => sites.includes(o.mine) && /Draft/.test(o.status)),
    inTransit: ctx.orders.filter(
      (o) => sites.includes(o.mine) && /air|ETA|Ordered/i.test(o.status) && !/Delivered/.test(o.status),
    ),
    openJobs: ctx.techJobs.filter((j) => j.status !== 'Completed' && siteNames.includes(j.site)),
    belowMin: ctx.inventory.filter(
      (i) => sites.some((k) => i.location === `${k} site store`) && i.qty_on_hand < i.min_level,
    ),
  };

  const rows: WipRow[] = [
    {
      key: 'drafts',
      queue: 'Drafted work orders awaiting approval',
      count: slices.drafts.length,
      detail:
        slices.drafts
          .slice(0, 8)
          .map((w) => `${w.wo_id} ${w.machine_id} · ${w.priority.split('-')[1] ?? w.priority}`)
          .join(' · ') || '—',
    },
    {
      key: 'requisitions',
      queue: 'Parts requisitions awaiting approval',
      count: slices.requisitions.length,
      detail:
        slices.requisitions
          .map((o) => `${o.order_no} ${o.part_no} → ${o.machine_id}`)
          .join(' · ') || '—',
    },
    {
      key: 'transit',
      queue: 'Parts in transit',
      count: slices.inTransit.length,
      detail:
        slices.inTransit
          .map((o) => `${o.order_no} ${o.part_no} (${o.status.split(' — ')[0].split(' · ')[0]})`)
          .join(' · ') || '—',
    },
    {
      key: 'jobs',
      queue: 'Open technician-app jobs',
      count: slices.openJobs.length,
      detail:
        slices.openJobs
          .slice(0, 8)
          .map((j) => `${j.job_id} ${j.machine} · ${j.technician.split(' ')[0]}`)
          .join(' · ') || '—',
    },
    {
      key: 'stock',
      queue: 'Store lines below minimum',
      count: slices.belowMin.length,
      detail: slices.belowMin.slice(0, 8).map((i) => `${i.part_no} @ ${i.location}`).join(' · ') || '—',
    },
  ];

  return { rows, slices };
}

/**
 * Demo line 2642. The morning prompt per site lead.
 *
 * Five sites carry one; Mount Read does not, and the demo prints `undefined`
 * there. The port prints nothing instead — a blank cell is a blank cell, and
 * the word "undefined" in a report that goes to a customer's superintendent is
 * the kind of thing that ends a demo.
 */
export const SOS_PROMPT: Readonly<Partial<Record<MineCode, string>>> = {
  KG: 'Which of the three drafted work orders (T-02, J-02, L-04) goes first, and can the weekend PM window be held?',
  BR: 'Is the L-07 module fitted tonight, and does the J-04 kit make the cut change?',
  MI: 'Approve WO-26-11912 and the empty-haul restriction before the 10:00 crib?',
  OH: 'Does S-04 come off the pattern for the hammer exchange on Thursday morning?',
  GD: 'Approve the ML-21 hose campaign and the S-09 motor requisition this morning?',
};

/** Demo line 2642. One site's evaluation row, and its week. */
export interface EvaluationRow {
  readonly mine: MineCode;
  readonly name: string;
  readonly week: MineWeek;
  /** Mean overall CSS at the site this month, or `null` with no responses. */
  readonly css: number | null;
  readonly cssResponses: number;
  /** The prompt for this site's lead, `''` where there is none. */
  readonly prompt: string;
}

/** Demo line 2642. One row per site in scope, in `MINE_ORDER`. */
export function evaluation(sites: readonly MineCode[], ctx: SosCtx): EvaluationRow[] {
  return sites.map((mine) => {
    const responses = ctx.css.filter((r) => r.Site === MINES[mine].name);
    return {
      mine,
      name: MINES[mine].name,
      week: mineWeek(mine, ctx),
      css: responses.length
        ? +(responses.reduce((a, r) => a + r.Overall, 0) / responses.length).toFixed(1)
        : null,
      cssResponses: responses.length,
      prompt: SOS_PROMPT[mine] ?? '',
    };
  });
}

/** The headline measures across the sites in scope, for the reporting week. */
export interface SosHeadline {
  /** Machines at the sites in scope. */
  readonly machines: number;
  /** Availability across the scope, on `mineWeek`'s own divisor. */
  readonly availability: number;
  readonly breakdowns: number;
  readonly workOrders: number;
  readonly reactive: number;
  /** Reactive share of the week's orders, 0–100, rounded. */
  readonly reactivePct: number;
  readonly downHours: number;
  readonly cost: number;
  readonly co2Tonnes: number;
  readonly diesel: number;
  readonly kwh: number;
}

/**
 * The headline tiles.
 *
 * Availability is recomputed from the machine-level hours rather than averaged
 * over the per-site percentages: a straight mean of six percentages would
 * weight Ochre Hill's nine machines the same as Gawler Deep's twenty-six.
 * `hours ÷ (machine-days × 2 × 9.5)` is `mineWeek`'s own formula, aggregated.
 */
export function headline(weeks: readonly MineWeek[]): SosHeadline {
  const hours = weeks.reduce((a, w) => a + w.machines.reduce((b, m) => b + m.hours, 0), 0);
  const days = weeks.reduce((a, w) => a + w.days, 0);
  const workOrders = weeks.reduce((a, w) => a + w.wos, 0);
  const reactive = weeks.reduce((a, w) => a + w.reactive, 0);
  return {
    machines: weeks.reduce((a, w) => a + w.n, 0),
    availability: days ? +((hours / (days * 2 * 9.5)) * 100).toFixed(1) : 0,
    breakdowns: weeks.reduce((a, w) => a + w.breakdowns, 0),
    workOrders,
    reactive,
    reactivePct: workOrders ? Math.round((reactive / workOrders) * 100) : 0,
    downHours: +weeks.reduce((a, w) => a + w.down_h, 0).toFixed(1),
    cost: weeks.reduce((a, w) => a + w.cost, 0),
    co2Tonnes: +weeks.reduce((a, w) => a + w.co2_t, 0).toFixed(1),
    diesel: weeks.reduce((a, w) => a + w.diesel, 0),
    kwh: weeks.reduce((a, w) => a + w.kwh, 0),
  };
}

/** Demo line 2705. Whether a URL value names a site in scope. */
export const isScope = (value: string | null): value is MineCode =>
  (MINE_ORDER as readonly string[]).includes(value ?? '');

/** The sites a scope covers: one, or all six in `MINE_ORDER`. */
export const scopeSites = (mine: MineCode | null): readonly MineCode[] =>
  mine ? [mine] : MINE_ORDER;

/** Demo line 2705. The report's standfirst line, per scope. */
export function scopeLine(sites: readonly MineCode[]): string {
  const machines = FLEET.filter((f) => sites.includes(f.mine)).length;
  return (
    `Wednesday 09 Sep 2026, 06:00 · ${sites.length} site${sites.length > 1 ? 's' : ''}, ` +
    `${machines} machines · compiled at 06:00 from the condition register, alerts, the ` +
    `technician app, approvals, the parts pipeline and the CSS feed. Four sections, as the ` +
    `original daily report had them: hotspots, activity, work in progress, evaluation and prompt.`
  );
}

/** Demo line 2646. The definitions footnote, verbatim. */
export const SOS_DEFINITIONS =
  `Availability = engine hours ÷ (shifts × 9.5 scheduled operating h), week to ${WK.end.slice(8)} Sep · ` +
  'CSS = mean overall score of in-app survey responses this month · ' +
  'Hotspots = machines in the very-poor or major-issue bands.';

/** Demo line 2707. Who the report goes to, and when. */
export const SOS_DISTRIBUTION =
  '06:00 to the Regional Service Manager and each site’s superintendent and planner ' +
  '(Outlook and the Teams channel). One prompt per site lead — the answer is expected by 08:00.';
